import { createContext, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import type { EditorThemeClasses, Klass, LexicalEditor, LexicalNode } from 'lexical';

import { LexicalBaseButton, LexicalDialogActions, LexicalTextInput } from '../ui';

type CellContextShape = {
  cellEditorConfig: null | CellEditorConfig;
  cellEditorPlugins: null | JSX.Element | JSX.Element[];
  set: (cellEditorConfig: null | CellEditorConfig, cellEditorPlugins: null | JSX.Element | JSX.Element[]) => void;
};

type CellEditorConfig = Readonly<{
  namespace: string;
  nodes?: ReadonlyArray<Klass<LexicalNode>>;
  onError: (error: Error, editor: LexicalEditor) => void;
  readOnly?: boolean;
  theme?: EditorThemeClasses;
}>;

const CellContext = createContext<CellContextShape>({
  cellEditorConfig: null,
  cellEditorPlugins: null,
  set: () => {},
});

export const TableContext = ({ children }: { children: JSX.Element }) => {
  const [contextValue, setContextValue] = useState<{
    cellEditorConfig: null | CellEditorConfig;
    cellEditorPlugins: null | JSX.Element | JSX.Element[];
  }>({
    cellEditorConfig: null,
    cellEditorPlugins: null,
  });
  return (
    <CellContext.Provider
      value={useMemo(
        () => ({
          cellEditorConfig: contextValue.cellEditorConfig,
          cellEditorPlugins: contextValue.cellEditorPlugins,
          set: (cellEditorConfig, cellEditorPlugins) => {
            setContextValue({ cellEditorConfig, cellEditorPlugins });
          },
        }),
        [contextValue.cellEditorConfig, contextValue.cellEditorPlugins],
      )}
    >
      {children}
    </CellContext.Provider>
  );
};

export const InsertTableDialog = ({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}): JSX.Element => {
  const { t } = useTranslation('text-editor');
  const [rows, setRows] = useState('5');
  const [columns, setColumns] = useState('5');
  const [isDisabled, setIsDisabled] = useState(true);

  useEffect(() => {
    const row = Number(rows);
    const column = Number(columns);
    if (row && row > 0 && row <= 500 && column && column > 0 && column <= 50) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [rows, columns]);

  const onClick = () => {
    activeEditor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns,
      rows,
    });

    onClose();
  };

  return (
    <Fragment>
      <LexicalTextInput
        placeholder={'# of rows (1-500)'}
        label={t('modals.rows')}
        onChange={setRows}
        value={rows}
        data-test-id="table-modal-rows"
        type="number"
      />
      <LexicalTextInput
        placeholder={'# of columns (1-50)'}
        label={t('modals.columns')}
        onChange={setColumns}
        value={columns}
        data-test-id="table-modal-columns"
        type="number"
      />
      <LexicalDialogActions data-test-id="table-model-confirm-insert">
        <LexicalBaseButton disabled={isDisabled} onClick={onClick}>
          {t('modals.confirm')}
        </LexicalBaseButton>
      </LexicalDialogActions>
    </Fragment>
  );
};
